import React, { useMemo, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Sun, MoonStar } from "lucide-react";
import { computeSolar, solarColor } from "../lib/solar";

export default function SolarScore({ open, onOpenChange, blueprint, season: seasonProp, setSeason: setSeasonProp }) {
  const [seasonLocal, setSeasonLocal] = useState("Equinox");
  const season = seasonProp ?? seasonLocal;
  const setSeason = setSeasonProp ?? setSeasonLocal;
  const data = useMemo(() => {
    const r = computeSolar(blueprint?.rooms || [], blueprint?.items || [], season);
    return r.scored.length ? r : null;
  }, [blueprint, season]);

  const color = solarColor;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg bg-[#161B22] border-[#30363D] text-[#F0F6FC] max-h-[88vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2 text-xl">
            <Sun className="w-5 h-5 text-[#F59E0B]" /> Solar Daylight Score
          </DialogTitle>
          <DialogDescription className="text-[#8B949E]">Estimated daylight per room from the sun path — south &amp; east exposure with windows score highest.</DialogDescription>
        </DialogHeader>
        {!data ? (
          <p className="text-sm text-[#8B949E]">Add rooms to analyse daylight.</p>
        ) : (
          <div className="space-y-3" data-testid="solar-results">
            <div className="flex items-center gap-2">
              <span className="text-xs text-[#8B949E] font-mono-cad">Season</span>
              <Select value={season} onValueChange={setSeason}>
                <SelectTrigger data-testid="solar-season-select" className="bg-[#0D1117] border-[#30363D] h-8 w-40 text-xs"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#161B22] border-[#30363D] text-[#F0F6FC]">
                  {["Summer", "Equinox", "Winter"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center justify-between rounded-xl border border-[#30363D] bg-[#0D1117] p-4">
              <div><div className="text-xs uppercase tracking-widest text-[#8B949E] font-mono-cad">Avg Daylight</div>
                <div className="font-heading text-3xl font-bold" style={{ color: color(data.avg) }}>{data.avg}<span className="text-lg">/100</span></div></div>
              <div className="text-right text-xs font-mono-cad text-[#8B949E]">{data.dark} room(s)<br />flagged dark</div>
            </div>
            <div className="space-y-2">
              {data.scored.map((r, i) => (
                <div key={i} className="rounded-lg border border-[#30363D] bg-[#0D1117] px-3 py-2" data-testid={`solar-room-${i}`}>
                  <div className="flex items-center justify-between text-sm mb-1.5">
                    <span className="flex items-center gap-2">{r.dark && <MoonStar className="w-3.5 h-3.5 text-[#F43F5E]" />}{r.name}</span>
                    <span className="font-mono-cad text-xs" style={{ color: color(r.score) }}>{r.score} · {r.sunHours}h/day · {r.faces} · {r.windows}w</span>
                  </div>
                  <div className="h-2 rounded-full bg-[#21262D] overflow-hidden">
                    <div className="h-full rounded-full transition-all" style={{ width: `${r.score}%`, background: color(r.score) }} />
                  </div>
                  {r.dark && <div className="text-[10px] text-[#F43F5E] mt-1 font-mono-cad">Stays dark — add a south/east window or exterior wall.</div>}
                </div>
              ))}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
